import React, { useState } from 'react';
import { Modal, Radio, Space, message, Spin, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faHeadset, faPlugCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { CopyOutlined, LoadingOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { useSelector } from 'react-redux';
import '@src/pages/formDrawer/drawer.css';

const GENERATE_SUPPORT_CODE = gql`
  mutation GenerateSupportCode($tenantId: String!, $accessLevel: String!) {
    generateSupportCode(tenantId: $tenantId, accessLevel: $accessLevel) {
      session {
        _id
        code
        accessLevel
        expiresAt
        status
      }
      tenantSocketToken
    }
  }
`;

const REVOKE_SUPPORT_SESSION = gql`
  mutation RevokeSupportSession($sessionId: String!) {
    revokeSupportSession(sessionId: $sessionId) {
      _id
      status
    }
  }
`;

export function SupportRequestModal({ visible, onClose, onSessionCreated, activeSession, onRevoke }) {
  const [accessLevel, setAccessLevel] = useState('read');
  const [generatedSession, setGeneratedSession] = useState(null);

  const tenantId = useSelector((state) => state?.userAuthReducer?.authenticatedUserDetail?.tenantId);

  const [generateCode, { loading: generating }] = useMutation(GENERATE_SUPPORT_CODE);
  const [revokeSession, { loading: revoking }] = useMutation(REVOKE_SUPPORT_SESSION);

  const isSessionActive = activeSession?.status === 'active';

  async function handleGenerate() {
    try {
      const { data } = await generateCode({ variables: { tenantId, accessLevel } });
      const { session, tenantSocketToken } = data.generateSupportCode;
      sessionStorage.setItem('supportTenantSocketToken', tenantSocketToken);
      setGeneratedSession(session);
      onSessionCreated?.(session);
    } catch (err) {
      message.error(err.message || 'Failed to generate code');
    }
  }

  async function handleRevoke() {
    try {
      await revokeSession({ variables: { sessionId: activeSession._id } });
      onRevoke?.();
      onClose?.();
    } catch {
      // socket disconnect clears state anyway
    }
  }

  function copyCode() {
    navigator.clipboard.writeText(generatedSession.code);
    message.success('Code copied!');
  }

  function handleClose() {
    setGeneratedSession(null);
    setAccessLevel('read');
    onClose?.();
  }

  const expiresIn = generatedSession
    ? Math.max(0, Math.round((new Date(generatedSession.expiresAt) - Date.now()) / 60000))
    : null;

  const headerTitle = isSessionActive
    ? 'Active Support Session'
    : generatedSession
      ? 'Share This Code'
      : 'Request WFC Support';

  return (
    <Modal
      open={visible}
      width={480}
      closable={false}
      footer={
        isSessionActive ? (
          <div style={{ padding: '16px 40px', display: 'flex', columnGap: 16, marginTop: -25 }}>
            <button
              className={revoking ? 'disabled-btn drawer-filled-btn' : 'drawer-filled-btn'}
              style={{ background: revoking ? undefined : '#ff4d4f', borderColor: revoking ? undefined : '#ff4d4f' }}
              disabled={revoking}
              onClick={handleRevoke}
            >
              {revoking
                ? <Spin indicator={<LoadingOutlined />} />
                : <><FontAwesomeIcon icon={faPlugCircleXmark} style={{ marginRight: 6 }} />End Session</>
              }
            </button>
            <button className="drawer-outlined-btn" onClick={handleClose}>
              Close
            </button>
          </div>
        ) : generatedSession ? (
          <div style={{ padding: '16px 40px', display: 'flex', columnGap: 16, marginTop: -25 }}>
            <button className="drawer-filled-btn" onClick={handleClose}>Done</button>
            <button className="drawer-outlined-btn" onClick={copyCode}>
              <CopyOutlined style={{ marginRight: 6 }} />Copy Code
            </button>
          </div>
        ) : (
          <div style={{ padding: '16px 40px', display: 'flex', columnGap: 16, marginTop: -25 }}>
            <button
              className={generating ? 'disabled-btn drawer-filled-btn' : 'drawer-filled-btn'}
              disabled={generating}
              onClick={handleGenerate}
            >
              {generating ? <Spin indicator={<LoadingOutlined />} /> : 'Generate Access Code'}
            </button>
            <button className="drawer-outlined-btn" onClick={handleClose}>Cancel</button>
          </div>
        )
      }
    >
      <div className="modal-header-title">
        <span>
          <FontAwesomeIcon icon={faHeadset} style={{ marginRight: 8 }} />
          {headerTitle}
        </span>
        <span onClick={handleClose}>
          <FontAwesomeIcon className="close" icon={faClose} />
        </span>
      </div>

      <div className="modal-body">

        {isSessionActive ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 18px', background: '#fff1f0',
              borderRadius: 6, border: '1px solid #ffccc7',
            }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: '#ff4d4f', display: 'inline-block',
                animation: 'support-pulse 1.5s infinite', flexShrink: 0,
              }} />
              <span style={{ fontSize: 14 }}>
                WFC Support is currently viewing your session.
              </span>
            </div>

            <div>
              <div className="text" style={{ marginBottom: 6 }}>Access level granted</div>
              {activeSession.accessLevel === 'full'
                ? <Tag color="red" style={{ fontSize: 13, padding: '2px 10px' }}>Full access — can view and edit</Tag>
                : <Tag color="orange" style={{ fontSize: 13, padding: '2px 10px' }}>Read only — view only</Tag>
              }
            </div>

            <div className="text">
              Clicking <strong>End Session</strong> will immediately disconnect the support agent.
            </div>
          </div>
        ) : generatedSession ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, paddingTop: 8 }}>
            <div className="text" style={{ textAlign: 'center' }}>
              Share this code with the WFC support agent:
            </div>

            <div style={{
              background: '#f5f8fa', borderRadius: 10,
              padding: '22px 40px', textAlign: 'center',
            }}>
              <span style={{
                fontSize: 44, fontWeight: 700,
                fontFamily: 'monospace', letterSpacing: 14,
                color: '#0091ae',
              }}>
                {generatedSession.code}
              </span>
            </div>

            <div className="text" style={{ fontSize: 12, textAlign: 'center' }}>
              Expires in <strong>{expiresIn}</strong> minute{expiresIn !== 1 ? 's' : ''} &nbsp;·&nbsp;
              Access: <strong>{accessLevel === 'full' ? 'Full' : 'Read only'}</strong>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="text">
              A WFC support agent will be able to view your session and help you. Choose the access level you want to grant:
            </div>

            <Radio.Group value={accessLevel} onChange={(e) => setAccessLevel(e.target.value)}>
              <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <Radio value="read">
                  <span style={{ fontWeight: 600, fontSize: 14 }}>Read only</span>
                  <div style={{ fontSize: 12, color: '#7c98b6', marginTop: 2 }}>
                    Support can view your data but cannot make changes.
                  </div>
                </Radio>
                <Radio value="full">
                  <span style={{ fontWeight: 600, fontSize: 14 }}>Full access</span>
                  <div style={{ fontSize: 12, color: '#7c98b6', marginTop: 2 }}>
                    Support can view and edit your data during the session.
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </div>
        )}

      </div>

      <style>{`
        @keyframes support-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </Modal>
  );
}
